// =============================================================================
// useCurrentLot — which LOT (tipping row) a capture on a run belongs to.
//
// The lots design (docs/plans/RETIP-PREPACKED-PALLETS-AND-RUNS-UPGRADE-
// 2026-09-07.md §2A) attributes every closed pallet and every reject to the
// tip it came from, so a retipped prepacked pallet's own packout can be read.
// Attribution is EXPLICIT — the row carries the lot the operator's chip named
// — with the run's LATEST tip as the default, so the normal flow costs no
// extra tap and the number is still exact. A run with no tips yet writes
// NULL (unassigned); the views show that as such rather than guessing.
//
// The choice is remembered per run, per tenant, per device: an operator who
// switches to "this pallet" keeps that until they switch back or the run
// changes. 'auto' = follow the latest tip.
// =============================================================================
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useTenantPersistedState } from '@/hooks/useTenantPersistedState'
import { AUTO_LOT, lotLabel, lotLabelsById, resolveLot, type LotSourceKind } from '@/lib/runLots'

export { AUTO_LOT }

export interface RunTipOption {
  tipping_id: string
  tipped_at:  string
  lot_key:    string
  label:      string
  kind:       LotSourceKind
}

/** The run's tips, newest first, labelled the way an operator names them.
 *  A tip loaded out as it is (mig 20261340000000) went whole into its own
 *  pallet, so it is no lot for anything else packed on the run: left out. */
export function useRunTips(runId: string | null | undefined) {
  return useQuery({
    queryKey: ['run-lot-sources', runId],
    enabled: !!runId,
    queryFn: async () => {
      const [{ data, error }, loadOuts] = await Promise.all([
        supabase
          .from('run_lot_sources')
          .select('tipping_id, tipped_at, lot_key, source_kind, source_pallet_id, source_pallet_ref, bin_number, client_ref_no, delivery_note_number, batch_number')
          .eq('run_id', runId!)
          .order('tipped_at', { ascending: false }),
        supabase
          .from('pallets')
          .select('source_tipping_id')
          .eq('run_id', runId!)
          .not('loaded_out_bin_id', 'is', null)
          .is('deleted_at', null)
          .range(0, 999),   // one run's load-out pallets: far fewer than a page
      ])
      if (error) throw error
      if (loadOuts.error) throw loadOuts.error
      const loadedOut = new Set((loadOuts.data ?? []).map(p => p.source_tipping_id as string | null))
      return (data ?? []).filter(r => !loadedOut.has(r.tipping_id as string)).map((r): RunTipOption => ({
        tipping_id: r.tipping_id as string,
        tipped_at:  r.tipped_at as string,
        lot_key:    r.lot_key as string,
        kind:       r.source_kind as LotSourceKind,
        label:      lotLabel({
          source_kind: r.source_kind as LotSourceKind, source_pallet_id: r.source_pallet_id, source_pallet_ref: r.source_pallet_ref,
          bin_number: r.bin_number, client_ref_no: r.client_ref_no, delivery_note_number: r.delivery_note_number, batch_number: r.batch_number,
        }),
      }))
    },
  })
}

/**
 * tipping_id → the operator's name for that lot, for the lots a pallet's
 * build-up lines name (nulls skipped). A tip that is gone (undone → the line's
 * lot went NULL) simply has no entry.
 */
export function useLotLabels(tippingIds: (string | null | undefined)[]) {
  const ids = [...new Set(tippingIds.filter((x): x is string => !!x))].sort()
  return useQuery({
    queryKey: ['lot-labels', ids],
    enabled: ids.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('run_lot_sources')
        .select('tipping_id, source_kind, source_pallet_id, source_pallet_ref, bin_number, client_ref_no, delivery_note_number, batch_number')
        .in('tipping_id', ids)
      if (error) throw error
      return lotLabelsById(data ?? [])
    },
  })
}

export function useCurrentLot(runId: string | null | undefined) {
  const tipsQ = useRunTips(runId)
  const tips = tipsQ.data ?? []
  const [choice, setChoice] = useTenantPersistedState<string>(`kf49.currentLot.${runId ?? 'none'}`, AUTO_LOT)
  const resolved = resolveLot(tips, choice)
  return useMemo(() => ({ tips, tipsQ, setChoice, ...resolved }), [tips, tipsQ, setChoice, resolved])
}
